'use client'

import { memo, useLayoutEffect, useRef, useSyncExternalStore, type ReactNode } from 'react'
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion'
import { cn } from '@/lib/utils'

const MOBILE_QUERY = '(max-width: 768px)'

const subscribeMobile = (onStoreChange: () => void) => {
  const media = window.matchMedia(MOBILE_QUERY)
  media.addEventListener('change', onStoreChange)
  return () => media.removeEventListener('change', onStoreChange)
}

const getMobileSnapshot = () => window.matchMedia(MOBILE_QUERY).matches
const getMobileServerSnapshot = () => false

/** baby: Mobile breakpoint without useEffect */
const useIsMobile = () =>
  useSyncExternalStore(subscribeMobile, getMobileSnapshot, getMobileServerSnapshot)

type ContainerScrollHeaderProps = {
  translate: MotionValue<number>
  titleComponent: ReactNode
}

const ContainerScrollHeader = memo(function ContainerScrollHeader({
  translate,
  titleComponent,
}: ContainerScrollHeaderProps) {
  return (
    <motion.div
      style={{ translateY: translate }}
      className="mx-auto max-w-5xl text-center"
    >
      {titleComponent}
    </motion.div>
  )
})

type ContainerScrollCardProps = {
  rotate: MotionValue<number>
  scale: MotionValue<number>
  children: ReactNode
  className?: string
}

/** Motion wrapper only: no border, shadow, or inner frame */
const ContainerScrollCard = memo(function ContainerScrollCard({
  rotate,
  scale,
  children,
  className,
}: ContainerScrollCardProps) {
  return (
    <motion.div
      style={{ rotateX: rotate, scale }}
      className={cn('relative mx-auto origin-top', className)}
    >
      {children}
    </motion.div>
  )
})

export type ContainerScrollProps = {
  titleComponent: ReactNode
  children: ReactNode
  className?: string
  cardClassName?: string
}

/**
 * Full hero scroll stack (title + card). Prefer ScrollTilt when you only need
 * the perspective animation on existing UI.
 */
export const ContainerScroll = memo(function ContainerScroll({
  titleComponent,
  children,
  className,
  cardClassName,
}: ContainerScrollProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  const reduceMotion = useReducedMotion()

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  })

  const rotate = useTransform(scrollYProgress, [0, 1], [20, 0])
  const scale = useTransform(
    scrollYProgress,
    [0, 1],
    isMobile ? [0.7, 0.9] : [1.05, 1],
  )
  const translate = useTransform(scrollYProgress, [0, 1], [0, -100])

  const staticScale = isMobile ? 0.9 : 1

  if (reduceMotion) {
    return (
      <div
        ref={containerRef}
        className={cn('relative flex flex-col items-center px-2 py-10 md:px-8', className)}
      >
        <div className="mx-auto w-full max-w-5xl text-center">{titleComponent}</div>
        <div
          className={cn('relative mx-auto mt-6 w-full max-w-[1060px]', cardClassName)}
          style={{ transform: `scale(${staticScale})` }}
        >
          {children}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex min-h-[52rem] items-start justify-center overflow-visible px-2 pt-4 md:min-h-[64rem] md:px-8 md:pt-8',
        className,
      )}
    >
      <div
        className="relative w-full overflow-visible py-8 md:py-16"
        style={{ perspective: '1000px' }}
      >
        <ContainerScrollHeader translate={translate} titleComponent={titleComponent} />
        <ContainerScrollCard rotate={rotate} scale={scale} className={cardClassName}>
          {children}
        </ContainerScrollCard>
      </div>
    </div>
  )
})

/** Small scroll slack below hero: room for tilt, no collapse (avoids overlap) */
const SCROLL_TILT_SLACK = 'h-[5vh] min-h-[2.5rem] md:h-[6vh] md:min-h-[3rem]' as const

const applyScrollTiltTransform = (
  element: HTMLDivElement | null,
  rotateDeg: number,
  scaleValue: number,
) => {
  if (!element) return
  element.style.transform = `perspective(1400px) rotateX(${rotateDeg}deg) scale(${scaleValue})`
  element.style.transformOrigin = 'top center'
}

type ScrollTiltLayerProps = {
  children: ReactNode
}

/**
 * Plain div + scroll-linked transform (no motion mount transition).
 * Natural document flow: no sticky pin, so content below never overlaps on scroll.
 */
const ScrollTiltLayer = memo(function ScrollTiltLayer({ children }: ScrollTiltLayerProps) {
  const runwayRef = useRef<HTMLDivElement>(null)
  const tiltRef = useRef<HTMLDivElement>(null)

  const { scrollYProgress } = useScroll({
    target: runwayRef,
    offset: ['start 0.88', 'end 0.35'],
  })

  const rotate = useTransform(scrollYProgress, [0, 0.55, 1], [18, 5, 0])
  const scale = useTransform(scrollYProgress, [0, 0.55, 1], [1.05, 1.01, 1])

  useMotionValueEvent(rotate, 'change', (rotateDeg) => {
    applyScrollTiltTransform(tiltRef.current, rotateDeg, scale.get())
  })

  useMotionValueEvent(scale, 'change', (scaleValue) => {
    applyScrollTiltTransform(tiltRef.current, rotate.get(), scaleValue)
  })

  // baby: Snap to current scroll position on mount: no intro animation
  useLayoutEffect(() => {
    applyScrollTiltTransform(tiltRef.current, rotate.get(), scale.get())
  }, [rotate, scale])

  return (
    <div ref={runwayRef} className="relative overflow-visible">
      <div
        ref={tiltRef}
        className="relative z-0 overflow-visible will-change-transform [backface-visibility:hidden]"
      >
        {children}
      </div>
      <div aria-hidden className={cn('pointer-events-none', SCROLL_TILT_SLACK)} />
    </div>
  )
})

export type ScrollTiltProps = {
  children: ReactNode
  className?: string
}

/**
 * Animation-only wrapper: tilts existing hero UI flat on scroll.
 * Uses a fixed-height scroll runway (no visible borders/shadows).
 */
export const ScrollTilt = memo(function ScrollTilt({ children, className }: ScrollTiltProps) {
  const reduceMotion = useReducedMotion()

  if (reduceMotion) {
    return <div className={className}>{children}</div>
  }

  return (
    <div className={cn('relative overflow-visible', className)}>
      <ScrollTiltLayer>{children}</ScrollTiltLayer>
    </div>
  )
})

export const Header = ContainerScrollHeader
export const Card = ContainerScrollCard
